/**
 * fuzzball-alert.js
 *
 * Site-wide replacement for `window.alert()` and `window.confirm()` — a
 * styled, centred, promise-based modal that visually matches the existing
 * `.existing-cart-modal` pattern in `sections/quiz.liquid`.
 *
 * Usage:
 *   window.fuzzballAlert('Message text')
 *   await window.fuzzballAlert('Message text', { title: 'Quiz completed!', buttonText: 'OK' })
 *
 *   const ok = await window.fuzzballConfirm('Remove cat?')
 *   const ok = await window.fuzzballConfirm('Remove cat?', { title, confirmText: 'Remove', cancelText: 'Keep' })
 *
 * Returns:
 *   - fuzzballAlert:   Promise<void>    — resolves on OK click, Escape, or backdrop click.
 *   - fuzzballConfirm: Promise<boolean> — resolves true on Confirm; false on Cancel, Escape, or backdrop click.
 *
 * Lazy-mounts a single shared modal node into <body> on first call and reuses
 * it. The actions row is rebuilt per open so a single singleton serves both
 * alert (one button) and confirm (primary + secondary) modes. Uses the canonical
 * `.quiz-modal-overlay` singleton as the backdrop; if the overlay isn't present
 * on the page (e.g. cart-only pages without the quiz section), one is created
 * defensively.
 */
(function () {
  'use strict';

  if (typeof window === 'undefined') return;
  if (window.fuzzballAlert && window.fuzzballConfirm) return; // idempotent

  var modalEl = null;
  var titleEl = null;
  var messageEl = null;
  var actionsEl = null;
  var overlayEl = null;
  var bodyScrollLockY = 0;
  var activePromise = null;
  var activeResolve = null;
  var activeMode = null; // 'alert' | 'confirm' | 'cta'
  var previouslyFocused = null;
  var keydownBound = false;
  var focusables = []; // current focusable buttons in actions row

  function getOrCreateOverlay() {
    var existing = document.querySelector('.quiz-modal-overlay');
    if (existing) return existing;
    var el = document.createElement('div');
    el.className = 'quiz-modal-overlay';
    el.innerHTML = '&nbsp;';
    document.body.appendChild(el);
    return el;
  }

  function mountModal() {
    if (modalEl) return;

    modalEl = document.createElement('div');
    modalEl.className = 'fuzzball-alert-modal';
    modalEl.setAttribute('role', 'dialog');
    modalEl.setAttribute('aria-modal', 'true');
    modalEl.setAttribute('aria-labelledby', 'FuzzballAlertTitle');
    // tabindex=-1 so we can focus the dialog container itself (announces it
    // to assistive tech without painting the action button's focus ring on
    // open — matches the visual idle state of .existing-cart-modal).
    modalEl.setAttribute('tabindex', '-1');
    // Inline styles to suppress the global `*:focus-visible` ring on the
    // container — focusing it is purely for AT announcement, never visual.
    // Inline beats stylesheet rules without needing !important or relying
    // on Shopify's SCSS recompile cache.
    modalEl.style.outline = 'none';
    modalEl.style.boxShadow = 'none';

    var content = document.createElement('div');
    content.className = 'fuzzball-alert-modal__content';

    titleEl = document.createElement('h3');
    titleEl.id = 'FuzzballAlertTitle';
    titleEl.className = 'fuzzball-alert-modal__title h4';

    messageEl = document.createElement('p');
    messageEl.className = 'fuzzball-alert-modal__message Md_Body_Regular';

    actionsEl = document.createElement('div');
    actionsEl.className = 'fuzzball-alert-modal__actions';

    content.appendChild(titleEl);
    content.appendChild(messageEl);
    content.appendChild(actionsEl);
    modalEl.appendChild(content);

    document.body.appendChild(modalEl);
  }

  function buildAlertActions(opts) {
    actionsEl.textContent = '';
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'button button--primary fuzzball-alert-modal__btn';
    btn.textContent = opts.buttonText || 'OK';
    btn.addEventListener('click', function () { resolveAndClose(undefined); });
    actionsEl.appendChild(btn);
    focusables = [btn];
  }

  // Single-CTA mode: one primary button that resolves a truthy value so the
  // caller can run a redirect/callback. Dismiss (Escape/backdrop) resolves
  // undefined — see onOverlayClick/onKeydown — so the two are distinguishable.
  function buildCtaActions(opts) {
    actionsEl.textContent = '';
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'button button--primary fuzzball-alert-modal__btn';
    btn.textContent = opts.buttonText || 'OK';
    btn.addEventListener('click', function () { resolveAndClose(true); });
    actionsEl.appendChild(btn);
    focusables = [btn];
  }

  function buildConfirmActions(opts) {
    actionsEl.textContent = '';
    var confirmBtn = document.createElement('button');
    confirmBtn.type = 'button';
    confirmBtn.className = 'button button--primary fuzzball-alert-modal__btn';
    confirmBtn.textContent = opts.confirmText || 'Confirm';
    confirmBtn.addEventListener('click', function () { resolveAndClose(true); });

    var cancelBtn = document.createElement('button');
    cancelBtn.type = 'button';
    cancelBtn.className = 'button button--secondary fuzzball-alert-modal__btn';
    cancelBtn.textContent = opts.cancelText || 'Cancel';
    cancelBtn.addEventListener('click', function () { resolveAndClose(false); });

    actionsEl.appendChild(confirmBtn);
    actionsEl.appendChild(cancelBtn);
    focusables = [confirmBtn, cancelBtn];
  }

  function onOverlayClick(e) {
    if (e.target === overlayEl) {
      resolveAndClose(activeMode === 'confirm' ? false : undefined);
    }
  }

  function onKeydown(e) {
    if (e.key === 'Escape' || e.keyCode === 27) {
      e.preventDefault();
      resolveAndClose(activeMode === 'confirm' ? false : undefined);
    } else if (e.key === 'Tab' || e.keyCode === 9) {
      // Trap focus within the current action buttons.
      if (!focusables.length) return;
      e.preventDefault();
      var current = document.activeElement;
      var idx = focusables.indexOf(current);
      var nextIdx;
      if (e.shiftKey) {
        nextIdx = idx <= 0 ? focusables.length - 1 : idx - 1;
      } else {
        nextIdx = idx === -1 || idx >= focusables.length - 1 ? 0 : idx + 1;
      }
      focusables[nextIdx].focus();
    }
  }

  function lockBodyScroll() {
    bodyScrollLockY = window.scrollY || window.pageYOffset || 0;
    document.body.style.position = 'fixed';
    document.body.style.top = '-' + bodyScrollLockY + 'px';
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';
  }

  function unlockBodyScroll() {
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.width = '';
    window.scrollTo(0, bodyScrollLockY);
  }

  function open(mode, message, opts) {
    opts = opts || {};
    activeMode = mode;
    mountModal();
    overlayEl = getOrCreateOverlay();

    titleEl.textContent = opts.title || 'Fuzzball';
    messageEl.textContent = message == null ? '' : String(message);

    if (mode === 'confirm') {
      buildConfirmActions(opts);
    } else if (mode === 'cta') {
      buildCtaActions(opts);
    } else {
      buildAlertActions(opts);
    }

    previouslyFocused = document.activeElement;

    overlayEl.classList.add('active');
    modalEl.classList.add('active');

    overlayEl.addEventListener('click', onOverlayClick);
    if (!keydownBound) {
      document.addEventListener('keydown', onKeydown, true);
      keydownBound = true;
    }

    lockBodyScroll();

    // Focus the dialog container itself (not the action button) so screen
    // readers announce the dialog but the button doesn't render its focus
    // ring on open — pixel-matches .existing-cart-modal's idle state. The
    // Tab handler in onKeydown moves focus into focusables[0] on first tab.
    window.requestAnimationFrame(function () {
      if (modalEl) {
        try { modalEl.focus({ preventScroll: true }); } catch (_) { modalEl.focus(); }
      }
    });
  }

  function close() {
    if (!modalEl) return;
    modalEl.classList.remove('active');
    if (overlayEl) {
      overlayEl.classList.remove('active');
      overlayEl.removeEventListener('click', onOverlayClick);
    }
    if (keydownBound) {
      document.removeEventListener('keydown', onKeydown, true);
      keydownBound = false;
    }
    unlockBodyScroll();

    if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
      try { previouslyFocused.focus(); } catch (_) { /* no-op */ }
    }
    previouslyFocused = null;
    activeMode = null;
    focusables = [];
  }

  function resolveAndClose(value) {
    if (!activeResolve) {
      close();
      return;
    }
    var r = activeResolve;
    activeResolve = null;
    activePromise = null;
    close();
    r(value);
  }

  function openWhenReady(mode, message, opts) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function () { open(mode, message, opts); }, { once: true });
    } else {
      open(mode, message, opts);
    }
  }

  function fuzzballAlert(message, opts) {
    // If a modal is already open, queue this call after the current one
    // resolves so we never lose a message.
    if (activePromise) {
      return activePromise.then(function () {
        return fuzzballAlert(message, opts);
      });
    }
    activePromise = new Promise(function (resolve) {
      activeResolve = resolve;
      openWhenReady('alert', message, opts);
    });
    return activePromise;
  }

  function fuzzballConfirm(message, opts) {
    if (activePromise) {
      return activePromise.then(function () {
        return fuzzballConfirm(message, opts);
      });
    }
    activePromise = new Promise(function (resolve) {
      activeResolve = resolve;
      openWhenReady('confirm', message, opts);
    });
    return activePromise;
  }

  // Single-CTA modal. Resolves `true` when the CTA button is clicked, and
  // `undefined` when dismissed (Escape/backdrop) — so the caller can redirect
  // on the CTA while a dismiss just closes and leaves the page untouched.
  function fuzzballCtaAlert(message, opts) {
    if (activePromise) {
      return activePromise.then(function () {
        return fuzzballCtaAlert(message, opts);
      });
    }
    activePromise = new Promise(function (resolve) {
      activeResolve = resolve;
      openWhenReady('cta', message, opts);
    });
    return activePromise;
  }

  window.fuzzballAlert = fuzzballAlert;
  window.fuzzballConfirm = fuzzballConfirm;
  window.fuzzballCtaAlert = fuzzballCtaAlert;
})();
